import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PuzzlePiece, Plus, ArrowRight, Hammer, Receipt, FilePdf } from "@phosphor-icons/react";
import API from "../../lib/api";
import { openAuthFile } from "../../lib/fileOpen";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { bespokeIcon, money, fmtDate, monthLabel, CHARGE_STATUS, REQUEST_STATUS } from "../../lib/bespoke";

function RequestDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ title: "", details: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/billing/bespoke/requests", form);
      toast.success("Request sent — the Fixlr team will come back to you with a quote");
      onSaved?.(data); onOpenChange(false); setForm({ title: "", details: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send the request"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="bespoke-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo]">Request a bespoke module</DialogTitle>
          <DialogDescription>Need something Fixlr doesn't do out of the box? Describe it and we'll quote a one-off build fee and, if it needs upkeep, a monthly fee. Nothing is charged until you agree.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">In a few words *</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} maxLength={120} placeholder="e.g. Waste list for quoting clearances" className="mt-1" data-testid="bespoke-request-title" /></div>
          <div><Label className="text-xs">What should it do? *</Label><Textarea value={form.details} onChange={(e) => setForm((f) => ({ ...f, details: e.target.value }))} rows={6} maxLength={4000} placeholder="Who uses it, what they do today, what you'd like the screen to show, anything it needs to connect to…" className="mt-1" data-testid="bespoke-request-details" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy || form.title.trim().length < 3 || form.details.trim().length < 10} data-testid="bespoke-request-send">{busy ? "Sending…" : "Send request"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Billing page: the tenant's bespoke modules, what's owed/paid for them, and their requests. */
export function BespokeModulesCard() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const load = useCallback(() => API.get("/billing/bespoke").then((r) => setData(r.data)).catch(() => setData({ modules: [], charges: [], requests: [], totals: {} })), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const active = data.modules.filter((m) => m.status === "active");
  const due = data.charges.filter((c) => c.status === "due");
  const charges = showAll ? data.charges : data.charges.slice(0, 5);
  const empty = active.length === 0 && data.requests.length === 0 && data.charges.length === 0;

  return (
    <div className="border border-border bg-card rounded-xl p-5 mb-8" data-testid="billing-bespoke-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center"><PuzzlePiece size={18} className="text-primary" weight="duotone" /></div>
          <div>
            <h2 className="text-base font-bold font-[Chivo]">Bespoke modules</h2>
            <p className="text-xs text-muted-foreground">Custom functionality built just for you, billed by invoice outside your plan.</p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="billing-bespoke-request-btn"><Plus size={14} className="mr-1" /> Request a module</Button>
      </div>

      {empty && <p className="text-sm text-muted-foreground mt-4" data-testid="billing-bespoke-empty">Nothing bespoke yet. If your business needs a screen or workflow Fixlr doesn't have, ask us — we quote a build fee and an optional monthly fee, and it appears in your menu once it's live.</p>}

      {active.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-3 mt-4" data-testid="billing-bespoke-modules">
          {active.map((m) => {
            const Icon = bespokeIcon(m.icon);
            const live = m.module_status === "live";
            return (
              <div key={m.assignment_id} className="rounded-lg border border-border p-3 flex gap-3" data-testid={`billing-bespoke-module-${m.key}`}>
                <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center shrink-0"><Icon size={18} weight="duotone" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap"><p className="font-semibold text-sm truncate">{m.name}</p>
                    <Badge className={`text-[9px] ${live ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" : "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"}`}>{live ? "Live" : <><Hammer size={10} className="mr-0.5 inline" /> Being built</>}</Badge></div>
                  {m.description && <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{m.description}</p>}
                  <p className="text-[11px] text-muted-foreground mt-1">{m.one_off_fee > 0 ? `${money(m.one_off_fee)} set-up` : "No set-up fee"}{m.monthly_fee > 0 ? ` · ${money(m.monthly_fee)}/month from ${monthLabel(m.monthly_from)}` : " · no monthly fee"}</p>
                  {live && m.path && <Link to={m.path} className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-1" data-testid={`billing-bespoke-open-${m.key}`}>Open <ArrowRight size={12} /></Link>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {data.charges.length > 0 && (
        <div className="mt-5" data-testid="billing-bespoke-charges">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Receipt size={12} /> Invoices</h3>
            <p className="text-xs" data-testid="billing-bespoke-due">{due.length ? <span className="text-amber-700 dark:text-amber-300 font-medium">{money(data.totals.due)} outstanding</span> : <span className="text-muted-foreground">Nothing outstanding</span>}</p>
          </div>
          <ul className="mt-2 divide-y divide-border rounded-lg border border-border">
            {charges.map((c) => (
              <li key={c.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`billing-bespoke-charge-${c.id}`}>
                <div className="flex-1 min-w-0"><p className="truncate">{c.description}</p><p className="text-[10px] text-muted-foreground">{c.status === "paid" ? `Paid ${fmtDate(c.paid_at)}${c.reference ? ` · ${c.reference}` : ""}` : c.status === "waived" ? "Waived" : `Due ${fmtDate(c.due_at)}`}</p></div>
                <span className="font-semibold font-[Chivo]">{money(c.amount)}</span>
                <Badge className={`text-[9px] ${CHARGE_STATUS[c.status]?.cls}`}>{c.overdue ? "Overdue" : CHARGE_STATUS[c.status]?.label}</Badge>
                {c.status !== "waived" && <button type="button" onClick={() => openAuthFile(`/billing/bespoke/charges/${c.id}/invoice.pdf`)} className="text-muted-foreground hover:text-primary" title={c.invoice_number ? `Invoice ${c.invoice_number}` : "Invoice PDF"} data-testid={`billing-bespoke-invoice-${c.id}`}><FilePdf size={16} /></button>}
              </li>
            ))}
          </ul>
          {data.charges.length > 5 && <button type="button" className="text-xs text-primary hover:underline mt-2" onClick={() => setShowAll((s) => !s)} data-testid="billing-bespoke-charges-toggle">{showAll ? "Show fewer" : `Show all ${data.charges.length}`}</button>}
          <p className="text-[11px] text-muted-foreground mt-2">These are invoiced by the Fixlr team and aren't taken from your card.</p>
        </div>
      )}

      {data.requests.length > 0 && (
        <div className="mt-5" data-testid="billing-bespoke-requests">
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Your requests</h3>
          <ul className="mt-2 space-y-2">
            {data.requests.map((r) => (
              <li key={r.id} className="rounded-lg border border-border px-3 py-2" data-testid={`billing-bespoke-request-${r.id}`}>
                <div className="flex items-center gap-2 flex-wrap"><p className="text-sm font-medium">{r.title}</p><Badge className={`text-[9px] ${REQUEST_STATUS[r.status]?.cls}`} data-testid={`billing-bespoke-request-status-${r.id}`}>{REQUEST_STATUS[r.status]?.label}</Badge><span className="text-[10px] text-muted-foreground">{fmtDate(r.created_at)}</span></div>
                {r.quote && (r.quote.one_off_fee > 0 || r.quote.monthly_fee > 0 || r.quote.note) && <p className="text-xs mt-1" data-testid={`billing-bespoke-request-quote-${r.id}`}>Quote: <b>{money(r.quote.one_off_fee)}</b> build fee{r.quote.monthly_fee > 0 ? <> + <b>{money(r.quote.monthly_fee)}</b>/month</> : ""}{r.quote.note ? ` — ${r.quote.note}` : ""}</p>}
                {r.status === "declined" && (r.history || []).slice(-1)[0]?.note && <p className="text-xs text-muted-foreground mt-1">{r.history.slice(-1)[0].note}</p>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <RequestDialog open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
}
